import { MouseEvent, MutableRefObject } from 'react';

/**
 * Количество запоминаемых перемещений мыши по меню
 */
const MOUSE_HISTORY_SIZE = 3;

/**
 * Точка, выраженная координатами X и Y
 */
export type Point = { x: number; y: number };

/**
 * Координаты 2-х вершин прямой линии
 */
type Line = {
    A: Point;
    B: Point;
};

/**
 * Координаты 3-х вершин треугольника
 */
type Triangle = {
    A: Point;
    B: Point;
    C: Point;
};

/**
 * Координаты 4-х вершин четырехугольника
 */
type Rectangle = {
    topLeft: Point;
    topRight: Point;
    bottomLeft: Point;
    bottomRight: Point;
};

/**
 * Создание линии из координат 2-х точек
 * @param pointA точка A
 * @param pointB точка B
 */
function getLine(pointA: Point, pointB: Point) {
    return {
        A: { ...pointA },
        B: { ...pointB },
    };
}

/**
 * Создание вектора из координат 2-х точек
 * @param pointA точка A
 * @param pointB точка B
 */
function getVector(pointA: Point, pointB: Point) {
    return {
        x: pointB.x - pointA.x,
        y: pointB.y - pointA.y,
    };
}

/**
 * Вычисление скалярного произведения вектора.
 * Это нужно для проверки сонаправленности векторов движения курсора мыши к сторонам треугольной области.
 * @param line координаты стороны треугольника
 * @param point координаты точки, в которой находится курсор мыши (вершина треугольника)
 */
function getPolarity(line: Line, point: Point) {
    const vectorA = getVector(line.A, line.B);
    const vectorB = getVector(line.A, point);

    const scalar = vectorA.x * vectorB.y - vectorA.y * vectorB.x;
    return scalar >= 0 ? 1 : -1;
}

/**
 * Проверка, что курсор мыши движется к открытому подменю
 * @param line координаты стороны треугольника
 * @param point1 координата точки, в которой курсов мыши был ранее (предыдущая позиция)
 * @param point2 координаты точки, в которой сейчас находится курсор мыши (текущая позиция)
 */
function checkSameDirection(line: Line, point1: Point, point2: Point): boolean {
    return getPolarity(line, point1) === getPolarity(line, point2);
}

/**
 * Создание вершин треугольной области, в которой будет проверяться нахождение мыши.
 * @param menuElementBox координаты вершин элемента меню, относительно которого будет высчитываться положение мыши
 * @param point координаты точки, в которой находится курсор мыши (вершина треугольника)
 */
function getTriangleZone(menuElementBox: Rectangle, point: Point): Triangle {
    return {
        A: {
            x: point.x,
            y: point.y,
        },
        B: {
            x: menuElementBox.topLeft.x,
            y: Math.min(0, menuElementBox.topLeft.y),
        },
        C: {
            x: menuElementBox.bottomLeft.x,
            y: Math.max(window.innerHeight, menuElementBox.bottomLeft.y),
        },
    };
}

/**
 * Получение координат вершин элемента меню
 * @param element элемент меню, координаты вершин которого требуется вычислить
 */
function getRectangle(element: Element) {
    const boundingBox = element.getBoundingClientRect();
    const scrollLeft = document.documentElement.scrollLeft;
    const scrollTop = document.documentElement.scrollTop;
    return {
        topLeft: {
            x: boundingBox.left + scrollLeft,
            y: boundingBox.top + scrollTop,
        },
        topRight: {
            x: boundingBox.left + scrollLeft + boundingBox.width,
            y: boundingBox.top + scrollTop,
        },
        bottomLeft: {
            x: boundingBox.left + scrollLeft,
            y: boundingBox.top + scrollTop + boundingBox.height,
        },
        bottomRight: {
            x: boundingBox.left + scrollLeft + boundingBox.width,
            y: boundingBox.top + scrollTop + boundingBox.height,
        },
    };
}

/**
 * Проверка перемещения мыши в сторону открытого меню.
 * Проверка идет внутри треугольной области от местоположения мыши до правого верхнего и правого нижнего краев меню.
 * @param menuElement элемент меню, относительно которого будет высчитываться положение мыши
 * @param mouseHistory история перемещения мыши по меню
 */
export function isInsideTriangle(menuElement?: Element | null, mouseHistory?: Point[]): boolean {
    if (!menuElement || !mouseHistory) {
        return false;
    }

    const mouseHistoryLength = mouseHistory.length;
    if (mouseHistoryLength < 2) {
        return false;
    }

    const currentMousePosition = mouseHistory[mouseHistoryLength - 1];
    const previousMousePosition = mouseHistory[0];

    if (!currentMousePosition || !previousMousePosition) {
        return false;
    }

    const menuBox = getRectangle(menuElement);
    const triangle = getTriangleZone(menuBox, previousMousePosition);
    const { A, B, C } = triangle;

    const triangleLineAB = getLine(A, B);
    const triangleLineAC = getLine(A, C);
    const triangleLineBC = getLine(B, C);

    return (
        checkSameDirection(triangleLineAB, C, currentMousePosition) &&
        checkSameDirection(triangleLineAC, B, currentMousePosition) &&
        checkSameDirection(triangleLineBC, A, currentMousePosition)
    );
}

/**
 * Получение позиции мыши
 * @param mouseEvent событие перемещения мыши
 */
function getMousePosition(mouseEvent: MouseEvent): Point {
    const nativeMouseEvent = mouseEvent.nativeEvent;

    if (nativeMouseEvent.pageX || nativeMouseEvent.pageY) {
        return { x: nativeMouseEvent.pageX, y: nativeMouseEvent.pageY };
    }

    if (nativeMouseEvent.clientX || nativeMouseEvent.clientY) {
        return {
            x: nativeMouseEvent.clientX + document.body.scrollLeft + document.documentElement.scrollLeft,
            y: nativeMouseEvent.clientY + document.body.scrollTop + document.documentElement.scrollTop,
        };
    }

    return { x: 0, y: 0 };
}

/**
 * Обновление истории перемещения мыши по меню
 * @param mouseEvent событие перемещения мыши
 * @param mouseHistoryRef история перемещения мыши по меню
 */
export function updateMouseHistory(
    mouseEvent: MouseEvent,
    mouseHistoryRef: MutableRefObject<Point[] | undefined>,
): void {
    if (!mouseHistoryRef.current) {
        mouseHistoryRef.current = [];
    }

    const mouseHistory = mouseHistoryRef.current;
    mouseHistory.push(getMousePosition(mouseEvent));

    if (mouseHistory.length > MOUSE_HISTORY_SIZE) {
        mouseHistory.shift();
    }
}
